/**
 * QUIPWA — Short Course → Long Course promotion helpers.
 *
 * Pure string work, kept out of the store so it can be tested directly. The
 * promotion itself (Drive folder + move + index update) lives in
 * notes-store.promoteToLongCourse.
 */

import { yamlScalar } from './frontmatter';

/**
 * Filename given to the module carrying a promoted note's original content.
 *
 * Only the *file* is renamed, so modules sort in order inside the course folder.
 * The note's title is deliberately left alone: it's what the user wrote, what
 * their `[[wikilinks]]` point at, and what they recognise the note by. Renaming
 * it to "Módulo 1" threw that away to gain nothing the filename doesn't already
 * give us.
 */
export const MODULE_ONE_FILENAME = 'modulo_1.md';

/**
 * Rewrite a single frontmatter field, leaving every other key (including ones
 * this app doesn't know about, e.g. from Obsidian) untouched. Adds a
 * frontmatter block if the file has none. The value is quoted when YAML needs
 * it (a conflict copy of `Con: dos puntos` must stay readable to Obsidian).
 */
export function withFrontmatterField(markdown: string, field: string, value: string): string {
  const line = `${field}: ${yamlScalar(value, field)}`;
  const match = markdown.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  if (!match) return `---\n${line}\n---\n${markdown}`;

  const lines = match[1]!.split('\n');
  const at = lines.findIndex((l) => new RegExp(`^${field}\\s*:`, 'i').test(l));
  if (at === -1) lines.push(line);
  else lines[at] = line;

  return `---\n${lines.join('\n')}\n---\n${match[2]}`;
}

export const withFrontmatterType = (markdown: string, type: string): string =>
  withFrontmatterField(markdown, 'type', type);

export const withFrontmatterTitle = (markdown: string, title: string): string =>
  withFrontmatterField(markdown, 'title', title);

/**
 * Folder name for a promoted course: the note's title, minus the characters
 * that would break the `name='…'` queries findFile/rebuildIndex build.
 */
export function courseFolderName(title: string): string {
  const clean = (title ?? '').replace(/['"\\/]/g, '').trim();
  return clean || 'Curso sin título';
}

/**
 * Resolve a `[[wikilink]]` / `![[embed]]` target title against the notes index.
 *
 * Titles are not unique, and promotion is what makes that routine: a promoted
 * note keeps the title the user gave it, and the course index created around it
 * carries the same one. A bare `find()` then returned whichever happened to come
 * first in the array.
 *
 * When several notes share a title, prefer the one that isn't a Long Course —
 * a link should land on the text somebody wrote, not on its table of contents.
 * That's the same reasoning that decides which file keeps the original id during
 * promotion.
 */
export function resolveNoteByTitle<T extends { title: string; type?: string | undefined }>(
  notes: readonly T[],
  target: string,
): T | undefined {
  const wanted = (target ?? '').trim().toLowerCase();
  if (!wanted) return undefined;

  const matches = notes.filter((n) => n.title.trim().toLowerCase() === wanted);
  if (matches.length <= 1) return matches[0];
  return matches.find((n) => n.type !== 'Long Course') ?? matches[0];
}
